import { useCallback, useEffect, useState } from "react";
import { getPushPublicKey, getPushStatus, subscribePush, unsubscribePush } from "@/lib/apiClient";
import { isIOS, isStandalone } from "@/hooks/useInstallPrompt";

const toKey = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const usePush = () => {
  const [state, setState] = useState({ loading: true, supported: supported(), configured: false, enabled: false, devices: 0, permission: supported() ? Notification.permission : "denied", busy: false });
  const needsInstall = isIOS() && !isStandalone();

  const refresh = useCallback(async () => {
    if (!supported()) return setState((s) => ({ ...s, loading: false }));
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      const st = await getPushStatus(sub?.endpoint);
      setState((s) => ({ ...s, loading: false, configured: st.configured, enabled: !!sub && st.this_device, devices: st.devices, permission: Notification.permission }));
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const enable = async () => {
    setState((s) => ({ ...s, busy: true }));
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState((s) => ({ ...s, permission: perm })); return perm; }
      const { public_key } = await getPushPublicKey();
      const reg = await navigator.serviceWorker.ready;
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(public_key) }));
      await subscribePush(sub.toJSON());
      await refresh();
      return "granted";
    } finally {
      setState((s) => ({ ...s, busy: false }));
    }
  };

  const disable = async () => {
    setState((s) => ({ ...s, busy: true }));
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await unsubscribePush(sub.endpoint).catch(() => {}); await sub.unsubscribe(); }
      await refresh();
    } finally {
      setState((s) => ({ ...s, busy: false }));
    }
  };

  return { ...state, needsInstall, enable, disable, refresh };
};
